/**
 * variableClassifier.js
 * Classifies skill condition variables as RACE-level (fixed for the whole race)
 * or MOMENT-level (change during the race).
 * Also provides enum label maps from Valores.txt.
 */

// Variables fixed for the entire race — incompatibility here = build-level conflict
export const RACE_VARIABLES = new Set([
    'distance_type',
    'ground_type',
    'grade',
    'track_id',
    'season',
    'weather',
    'ground_condition',
    'course_distance',
    'rotation',
    'time',
    'is_dirtgrade',
    'is_basis_distance',
    'running_style',
    'motivation',
]);

// Variables that change during the race — incompatibility here = moment conflict only
export const MOMENT_VARIABLES = new Set([
    'corner',
    'corner_random',
    'corner_count',
    'all_corner_random',
    'phase',
    'phase_random',
    'phase_corner_random',
    'phase_firstquarter_random',
    'phase_laterhalf_random',
    'order',
    'order_rate',
    'distance_rate',
    'distance_rate_after_random',
    'hp_per',
    'is_last_straight',
    'is_finalcorner',
    'straight_front_type',
    'is_overtake',
    'slope',
    'down_slope_random',
    'lastspurt',
    'furlong',
    'lane_type',
    'bashin_diff_behind',
    'bashin_diff_infront',
    'behind_near_lane_time',
    'behind_near_lane_time_set1',
    'infront_near_lane_time',
    'blocked_front',
    'blocked_front_continuetime',
    'blocked_side_continuetime',
    'distance_diff_top',
    'distance_diff_top_float',
    'distance_diff_rate',
    'change_order_onetime',
    'change_order_up_end_after',
    'change_order_up_finalcorner_after',
    'change_order_up_middle',
    'compete_fight_count',
    'activate_count_all',
    'activate_count_all_team',
    'activate_count_start',
    'activate_count_middle',
    'activate_count_later_half',
    'activate_count_end_after',
    'activate_count_heal',
    'accumulatetime',
    'is_activate_any_skill',
    'is_activate_heal_skill',
    'is_move_lane',
    'base_speed',
    'base_stamina',
    'base_power',
    'base_guts',
    'base_wiz',
    'fan_count',
]);

/**
 * Returns 'race' | 'moment' | 'unknown' for a given variable name.
 */
export const classifyVariable = (variableName) => {
    if (RACE_VARIABLES.has(variableName)) return 'race';
    if (MOMENT_VARIABLES.has(variableName)) return 'moment';
    return 'moment'; // Default to moment (safer assumption)
};

// ── Enum label maps (from Valores.txt) ────────────────────────────────────────

export const ENUM_LABELS = {
    corner: {
        0: 'Not a Corner',
        1: '1st Corner',
        2: '2nd Corner',
        3: '3rd Corner',
        4: '4th Corner',
    },
    corner_random: {
        1: '1st Corner',
        2: '2nd Corner',
        3: '3rd Corner',
        4: '4th Corner',
    },
    distance_type: {
        1: 'Sprint',
        2: 'Mile',
        3: 'Medium',
        4: 'Long',
    },
    grade: {
        100: 'G1',
        200: 'G2',
        300: 'G3',
        400: 'OP',
        700: 'Pre-OP',
        800: 'Maiden',
        900: 'Debut',
        999: 'Daily',
    },
    ground_condition: {
        1: 'Good',
        2: 'Slightly Heavy',
        3: 'Heavy',
        4: 'Bad',
    },
    ground_type: {
        1: 'Turf',
        2: 'Dirt',
    },
    phase: {
        0: 'Early-Race',
        1: 'Mid-Race',
        2: 'Late-Race',
        3: 'Last Spurt',
    },
    phase_random: {
        0: 'Early-Race',
        1: 'Mid-Race',
        2: 'Late-Race',
        3: 'Last Spurt',
    },
    phase_corner_random: {
        0: 'Early-Race',
        1: 'Mid-Race',
        2: 'Late-Race',
        3: 'Last Spurt',
    },
    phase_firstquarter_random: {
        0: 'Early-Race',
        1: 'Mid-Race',
        2: 'Late-Race',
        3: 'Last Spurt',
    },
    phase_laterhalf_random: {
        0: 'Early-Race',
        1: 'Mid-Race',
        2: 'Late-Race',
        3: 'Last Spurt',
    },
    rotation: {
        1: 'Clockwise',
        2: 'Counterclockwise',
    },
    running_style: {
        1: 'Front Runner',
        2: 'Pace Chaser',
        3: 'Late Surger',
        4: 'End Closer',
    },
    season: {
        1: 'Spring',
        2: 'Summer',
        3: 'Fall',
        4: 'Winter',
        5: 'Cherry Blossom',
    },
    slope: {
        0: 'Flat',
        1: 'Uphill',
        2: 'Downhill',
    },
    weather: {
        1: 'Sunny',
        2: 'Cloudy',
        3: 'Rainy',
        4: 'Snowy',
    },
    motivation: {
        1: 'Terrible',
        2: 'Bad',
        3: 'Normal',
        4: 'Good',
        5: 'Perfect',
    },
    lastspurt: {
        0: 'No stamina to finish',
        1: 'Enough for base speed',
        2: 'Enough for max speed',
    },
    is_basis_distance: {
        0: 'Non-core distance',
        1: 'Core distance',
    },
    is_dirtgrade: {
        0: 'False',
        1: 'True',
    },
    track_id: {
        10001: 'Sapporo',
        10002: 'Hakodate',
        10003: 'Niigata',
        10004: 'Fukushima',
        10005: 'Nakayama',
        10006: 'Tokyo',
        10007: 'Chukyo',
        10008: 'Kyoto',
        10009: 'Hanshin',
        10010: 'Kokura',
        10101: 'Ooi',
        10103: 'Kawasaki',
        10104: 'Funabashi',
        10105: 'Morioka',
    },
    time: {
        0: 'Any',
        1: 'Morning',
        2: 'Daytime',
        3: 'Evening',
        4: 'Night',
    },
};

/**
 * Returns a human-readable label for an enum value.
 * Falls back to the raw value if no mapping exists.
 */
export const getEnumLabel = (variableName, value) => {
    const map = ENUM_LABELS[variableName];
    if (!map) return String(value);
    return map[value] !== undefined ? map[value] : String(value);
};

/**
 * Returns true if a variable has known enum values (not continuous).
 */
export const isEnumVariable = (variableName) => {
    return variableName in ENUM_LABELS;
};

/**
 * Returns true if a variable is boolean (0/1 with specific meaning).
 */
export const isBooleanVariable = (variableName) => {
    const boolVars = new Set([
        'is_last_straight', 'is_finalcorner', 'is_overtake',
        'blocked_front', 'is_move_lane', 'is_activate_any_skill',
        'is_activate_heal_skill', 'is_basis_distance', 'is_dirtgrade',
        'always',
    ]);
    return boolVars.has(variableName);
};
